/**
 * The POS App's API surface.
 *
 * `@sonauto/api-client` owns the transport (bearer token, single-flight
 * refresh, idempotency keys, typed errors). It also ships typed wrappers, but
 * those were written for the Admin app, which only READS orders and payments.
 * The POS is the app that WRITES them, so the mutation wrappers live here — on
 * top of the same `ApiClient`, never around a raw `fetch`.
 *
 * HONESTY RULE (identical to the one in the api-client): a function exists here
 * if and only if a handler exists in `apps/api-worker/src/routes/*.ts`. The
 * gaps are documented at the bottom of this file and surfaced in the UI as
 * "cần endpoint X" placeholders, never as fake data.
 */

import { createApiClient, newIdempotencyKey } from '@sonauto/api-client';
import type { ApiClient, AuthTokens, RequestOptions } from '@sonauto/api-client';
import type {
  AddItemsResponse,
  ApprovalCountResponse,
  ApprovalDecisionResponse,
  ApprovalsResponse,
  AreasResponse,
  CancelItemResponse,
  CountItemsInput,
  DeviceClaimResponse,
  DeviceMeResponse,
  InvIngredientsResponse,
  InvPoDetailResponse,
  InvPoListResponse,
  InvSuppliersResponse,
  InvWarehousesResponse,
  IssueBody,
  IssueResponse,
  PickupQueueResponse,
  QrTokenIssueResponse,
  QrTokenRevokeResponse,
  RealtimeTokenResponse,
  ReceiptBody,
  ReceiptResponse,
  StockCountDetailResponse,
  StockCountStartResponse,
  StockCountsResponse,
  StockResponse,
  TableOpResponse,
  WastageBody,
  WastageResponse,
  CashSessionDetailResponse,
  CategoriesResponse,
  CloseCashSessionResponse,
  CloseSessionResponse,
  ConfirmOrderResponse,
  CreateOrderBody,
  CreateOrderResponse,
  CreatePaymentBody,
  CreatePaymentResponse,
  CustomerFeedbackResponse,
  CustomerMenuResponse,
  CustomerOrderItemInput,
  CustomerOrdersResponse,
  CustomerRequestCode,
  CustomerRequestsResponse,
  CustomerServiceRequestResponse,
  CustomerSessionResponse,
  CustomerSetLocaleResponse,
  CustomerSubmitOrderResponse,
  CustomerSubmitOrderResponse as SubmitOrderResp,
  CustomerUiStringsResponse,
  DeviceActivateResponse,
  DiscountResponse,
  FloorMapResponse,
  GiftItemResponse,
  InvoiceResponse,
  KdsBoardResponse,
  LineInput,
  LoginRequest,
  LoginResponse,
  MeResponse,
  OpenCashSessionResponse,
  OpenSessionResponse,
  OrderListResponse,
  ProductAvailabilityResponse,
  ProductDetailResponse,
  ProductListResponse,
  ProductionActionResponse,
  SessionDetailResponse,
  StationLoadResponse,
  StationsResponse,
  TransferBody,
  TransferResponse,
} from './types';

/** `/api` in dev (Vite proxies it to :8787); an absolute origin in production. */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export interface CreatePosClientInput {
  readonly getTokens: () => AuthTokens | null;
  readonly setTokens: (tokens: AuthTokens | null) => void;
  readonly onUnauthenticated: () => void;
  readonly onRequestSettled?: ApiClientOptionsSettled;
}

type ApiClientOptionsSettled = NonNullable<Parameters<typeof createApiClient>[0]['onRequestSettled']>;

export function createPosClient(input: CreatePosClientInput): ApiClient {
  return createApiClient({
    baseUrl: API_BASE_URL,
    getTokens: input.getTokens,
    setTokens: input.setTokens,
    onUnauthenticated: input.onUnauthenticated,
    // 12s, not the 20s default. A cashier holding a queue of six people needs
    // the failure fast so they can switch to the offline outbox; waiting twenty
    // seconds for a dead network is worse than failing in twelve.
    timeoutMs: 12_000,
    clientVersion: import.meta.env.VITE_APP_VERSION ?? 'pos-dev',
    ...(input.onRequestSettled === undefined ? {} : { onRequestSettled: input.onRequestSettled }),
  });
}

type Extra = Pick<RequestOptions, 'signal' | 'reason' | 'idempotencyKey' | 'timeoutMs'>;

function extra(o: Extra | undefined): RequestOptions {
  return o === undefined ? {} : { ...o };
}

const seg = (s: string): string => encodeURIComponent(s);

/**
 * The POS endpoint surface.
 *
 * Grouped by the route file that implements each handler so a reviewer can
 * check the contract without leaving this file.
 */
export function createPosApi(client: ApiClient) {
  return {
    client,

    // -----------------------------------------------------------------------
    // auth (routes/auth.ts)
    // -----------------------------------------------------------------------
    auth: {
      login: (body: LoginRequest, o?: Extra): Promise<LoginResponse> =>
        client.post<LoginResponse>('/v1/auth/login', { ...extra(o), body, anonymous: true, idempotent: false }),
      me: (o?: Extra): Promise<MeResponse> => client.get<MeResponse>('/v1/auth/me', extra(o)),
      logout: (o?: Extra): Promise<{ ok: true }> =>
        client.post<{ ok: true }>('/v1/auth/logout', { ...extra(o), idempotent: false }),
      switchBranch: (branchId: string, o?: Extra): Promise<{ ok: true; activeBranchId: string }> =>
        client.post<{ ok: true; activeBranchId: string }>('/v1/auth/switch-branch', {
          ...extra(o),
          body: { branchId },
          idempotent: false,
        }),
    },

    // -----------------------------------------------------------------------
    // organisation (routes/organization.ts)
    // -----------------------------------------------------------------------
    org: {
      /** The floor map. Geometry is nested (`pos.x`, `size.width`). */
      tables: (branchId: string, query?: { areaId?: string }, o?: Extra): Promise<FloorMapResponse> =>
        client.get<FloorMapResponse>(`/v1/branches/${seg(branchId)}/tables`, {
          ...extra(o),
          query: query ?? {},
        }),
      /**
       * Zone headings. Requires `area.view`, which CASHIER/SERVER roles do NOT
       * hold — callers must tolerate a 403 and fall back to ungrouped tables.
       */
      areas: (branchId: string, o?: Extra): Promise<AreasResponse> =>
        client.get<AreasResponse>(`/v1/branches/${seg(branchId)}/areas`, extra(o)),
      /** Requires `branch.view`; a kitchen role does not hold it (see gaps). */
      stations: (query?: { branchId?: string; activeOnly?: boolean }, o?: Extra): Promise<StationsResponse> =>
        client.get<StationsResponse>('/v1/stations', { ...extra(o), query: query ?? {} }),
      /**
       * SELF-SERVICE claim (routes/qr.ts): UNAUTHENTICATED — the 6-character
       * code is the credential. Single-use, rate-limited, 24h expiry. Returns
       * the mode + branch + station this terminal was provisioned for.
       */
      claimDevice: (
        body: {
          activationCode: string;
          deviceName: string;
          platform?: string | null;
          fingerprint?: string | null;
          turnstileToken?: string | null;
        },
        o?: Extra,
      ): Promise<DeviceClaimResponse> =>
        client.post<DeviceClaimResponse>('/v1/devices/claim', {
          ...extra(o),
          body,
          anonymous: true,
          idempotent: false,
        }),

      /**
       * The CURRENT session's device record (routes/qr.ts). Deliberately NOT
       * behind `device.view` — this is how a cashier session finally learns its
       * own `deviceMode`. 404 when the session has no bound device.
       */
      deviceMe: (o?: Extra): Promise<DeviceMeResponse> => client.get<DeviceMeResponse>('/v1/devices/me', extra(o)),

      /**
       * Manager-led activation (routes/organization.ts): takes the device ID in
       * the PATH and needs `device.manage`. Kept as the fallback path.
       */
      activateDevice: (
        deviceId: string,
        body: { activationCode: string; fingerprint?: string | null; platform?: string | null },
        o?: Extra,
      ): Promise<DeviceActivateResponse> =>
        client.post<DeviceActivateResponse>(`/v1/devices/${seg(deviceId)}/activate`, {
          ...extra(o),
          body,
          idempotent: false,
        }),
    },

    // -----------------------------------------------------------------------
    // menu (routes/menu.ts)
    // -----------------------------------------------------------------------
    menu: {
      categories: (query: { branchId?: string; activeOnly?: boolean }, o?: Extra): Promise<CategoriesResponse> =>
        client.get<CategoriesResponse>('/v1/menu/categories', { ...extra(o), query }),
      products: (
        query: { page?: number; pageSize?: number; categoryId?: string; status?: string; search?: string },
        o?: Extra,
      ): Promise<ProductListResponse> => client.get<ProductListResponse>('/v1/products', { ...extra(o), query }),
      product: (id: string, o?: Extra): Promise<ProductDetailResponse> =>
        client.get<ProductDetailResponse>(`/v1/products/${seg(id)}`, extra(o)),
      /** Báo hết món / mở bán lại. Offline-capable per the permission catalogue. */
      setAvailability: (
        id: string,
        body: {
          branchId: string;
          isAvailable: boolean;
          businessDate?: string | null;
          outOfStockReason?: string | null;
          reasonText?: string | null;
          remainingQty?: number | null;
          stationId?: string | null;
          clientTime?: number | null;
        },
        o?: Extra,
      ): Promise<ProductAvailabilityResponse> =>
        client.patch<ProductAvailabilityResponse>(`/v1/products/${seg(id)}/availability`, {
          ...extra(o),
          body,
          idempotent: false,
        }),
    },

    // -----------------------------------------------------------------------
    // orders (routes/orders.ts)
    // -----------------------------------------------------------------------
    orders: {
      openSession: (
        body: {
          branchId: string;
          tableId?: string | null;
          areaId?: string | null;
          sessionType?: string;
          guestCount?: number;
          note?: string | null;
          clientUuid?: string | null;
        },
        o?: Extra,
      ): Promise<OpenSessionResponse> =>
        // Idempotency-Key REQUIRED by the API; the client generates one.
        client.post<OpenSessionResponse>('/v1/dining-sessions', { ...extra(o), body }),

      /** The bill screen: session + orders + items + balance in one call. */
      session: (id: string, o?: Extra): Promise<SessionDetailResponse> =>
        client.get<SessionDetailResponse>(`/v1/dining-sessions/${seg(id)}`, extra(o)),

      closeSession: (id: string, o?: Extra): Promise<CloseSessionResponse> =>
        client.post<CloseSessionResponse>(`/v1/dining-sessions/${seg(id)}/close`, { ...extra(o), idempotent: false }),

      create: (body: CreateOrderBody, o?: Extra): Promise<CreateOrderResponse> =>
        client.post<CreateOrderResponse>('/v1/orders', { ...extra(o), body }),

      /** "Gửi bếp". The only send-to-station endpoint; fires everything unsent. */
      confirm: (id: string, o?: Extra): Promise<ConfirmOrderResponse> =>
        client.post<ConfirmOrderResponse>(`/v1/orders/${seg(id)}/confirm`, { ...extra(o), idempotent: false }),

      /** Gọi thêm món. NOT idempotency-protected — the UI must lock the button. */
      addItems: (id: string, body: { priceTier?: string; items: LineInput[] }, o?: Extra): Promise<AddItemsResponse> =>
        client.post<AddItemsResponse>(`/v1/orders/${seg(id)}/items`, { ...extra(o), body, idempotent: false }),

      /**
       * WARNING: this PATCH behaves like a PUT for note/spice/course/priority —
       * omitting a field WIPES it. Always send the current values.
       */
      patchItem: (
        orderId: string,
        itemId: string,
        body: {
          quantity?: number;
          noteVi?: string | null;
          spiceLevel?: number | null;
          courseNo?: number;
          priority?: number;
          expectedVersion?: number;
        },
        o?: Extra,
      ): Promise<{ id: string; quantity: number; quantityMilli: number; totals: unknown }> =>
        client.patch(`/v1/orders/${seg(orderId)}/items/${seg(itemId)}`, { ...extra(o), body, idempotent: false }),

      /** Hủy món. `reason` is MANDATORY (403 REASON_REQUIRED without it). */
      cancelItem: (orderId: string, itemId: string, reason: string, o?: Extra): Promise<CancelItemResponse> =>
        client.del<CancelItemResponse>(`/v1/orders/${seg(orderId)}/items/${seg(itemId)}`, {
          ...extra(o),
          reason,
          idempotent: false,
        }),

      cancel: (id: string, reason: string, o?: Extra): Promise<{ id: string; status: string; cancelledItems: number }> =>
        client.post(`/v1/orders/${seg(id)}/cancel`, { ...extra(o), reason, idempotent: false }),

      /** Giảm giá. `reason` MANDATORY. 403 with maxBp when over the ceiling. */
      discount: (
        id: string,
        body: {
          discountType?: string;
          valueBp?: number | null;
          amountMinor?: number | null;
          orderItemId?: string | null;
        },
        reason: string,
        o?: Extra,
      ): Promise<DiscountResponse> =>
        client.post<DiscountResponse>(`/v1/orders/${seg(id)}/discount`, {
          ...extra(o),
          body,
          reason,
          idempotent: false,
        }),

      /** Tặng món. The reason header IS the comp reason stored on the line. */
      giftItem: (id: string, orderItemId: string, reason: string, o?: Extra): Promise<GiftItemResponse> =>
        client.post<GiftItemResponse>(`/v1/orders/${seg(id)}/gift-item`, {
          ...extra(o),
          body: { orderItemId },
          reason,
          idempotent: false,
        }),

      /** All six of move/merge/split (table and bill) go through this one route. */
      transfer: (body: TransferBody, reason: string, o?: Extra): Promise<TransferResponse> =>
        client.post<TransferResponse>('/v1/orders/transfer', { ...extra(o), body, reason, idempotent: false }),

      list: (
        query: {
          branchId?: string;
          businessDate?: string;
          status?: string;
          paymentStatus?: string;
          diningSessionId?: string;
          limit?: number;
          page?: number;
        },
        o?: Extra,
      ): Promise<OrderListResponse> => client.get<OrderListResponse>('/v1/orders', { ...extra(o), query }),

      /**
       * Issue a QR ordering token for a session (routes/qr.ts). The plaintext
       * token and the printable `slip` exist ONLY in this response — the server
       * stores a hash. Requires `table.open`.
       */
      issueQrToken: (
        sessionId: string,
        body?: { printerId?: string | null },
        o?: Extra,
      ): Promise<QrTokenIssueResponse> =>
        client.post<QrTokenIssueResponse>(`/v1/dining-sessions/${seg(sessionId)}/qr-token`, {
          ...extra(o),
          body: body ?? {},
          idempotent: false,
        }),

      /** Kill EVERY active token of a session. `reason` MANDATORY. */
      revokeQrTokens: (sessionId: string, reason: string, o?: Extra): Promise<QrTokenRevokeResponse> =>
        client.post<QrTokenRevokeResponse>(`/v1/dining-sessions/${seg(sessionId)}/qr-token/revoke`, {
          ...extra(o),
          reason,
          idempotent: false,
        }),
    },

    // -----------------------------------------------------------------------
    // table operations (routes/tables-ops.ts)
    // -----------------------------------------------------------------------
    tables: {
      /** "Bàn đã dọn xong." Offline-queueable (kind TABLE_CLEAN). */
      clean: (tableId: string, o?: Extra): Promise<TableOpResponse> =>
        client.post<TableOpResponse>(`/v1/tables/${seg(tableId)}/clean`, { ...extra(o), body: {} }),
      /** "Bàn đã setup xong." Offline-queueable (kind TABLE_SETUP). */
      setup: (tableId: string, o?: Extra): Promise<TableOpResponse> =>
        client.post<TableOpResponse>(`/v1/tables/${seg(tableId)}/setup`, { ...extra(o), body: {} }),
      /** The takeaway "mời nhận" board. Requires `order.view`. */
      pickupQueue: (branchId: string, o?: Extra): Promise<PickupQueueResponse> =>
        client.get<PickupQueueResponse>(`/v1/branches/${seg(branchId)}/pickup-queue`, extra(o)),
    },

    // -----------------------------------------------------------------------
    // approvals (routes/approvals.ts)
    // -----------------------------------------------------------------------
    approvals: {
      /** Items the caller could actually decide; anything else is filtered out. */
      list: (query?: { type?: string; status?: string; branchId?: string }, o?: Extra): Promise<ApprovalsResponse> =>
        client.get<ApprovalsResponse>('/v1/approvals', { ...extra(o), query: query ?? {} }),
      count: (query?: { branchId?: string }, o?: Extra): Promise<ApprovalCountResponse> =>
        client.get<ApprovalCountResponse>('/v1/approvals/count', { ...extra(o), query: query ?? {} }),
      /** `reason` MANDATORY in both directions (x-action-reason header). */
      approve: (
        type: string,
        id: string,
        reason: string,
        body?: { note?: string | null; adjustmentPointsMilli?: number },
        o?: Extra,
      ): Promise<ApprovalDecisionResponse> =>
        client.post<ApprovalDecisionResponse>(`/v1/approvals/${seg(type)}/${seg(id)}/approve`, {
          ...extra(o),
          body: body ?? {},
          reason,
          idempotent: false,
        }),
      reject: (
        type: string,
        id: string,
        reason: string,
        body?: { note?: string | null },
        o?: Extra,
      ): Promise<ApprovalDecisionResponse> =>
        client.post<ApprovalDecisionResponse>(`/v1/approvals/${seg(type)}/${seg(id)}/reject`, {
          ...extra(o),
          body: body ?? {},
          reason,
          idempotent: false,
        }),
    },

    // -----------------------------------------------------------------------
    // inventory (routes/inventory.ts) — the WAREHOUSE_TERMINAL slice
    // -----------------------------------------------------------------------
    inventory: {
      warehouses: (query?: { branchId?: string; type?: string }, o?: Extra): Promise<InvWarehousesResponse> =>
        client.get<InvWarehousesResponse>('/v1/inventory/warehouses', { ...extra(o), query: query ?? {} }),
      suppliers: (query?: { q?: string; activeOnly?: boolean }, o?: Extra): Promise<InvSuppliersResponse> =>
        client.get<InvSuppliersResponse>('/v1/inventory/suppliers', { ...extra(o), query: query ?? {} }),
      ingredients: (
        query?: { q?: string; category?: string; activeOnly?: boolean; limit?: number; offset?: number },
        o?: Extra,
      ): Promise<InvIngredientsResponse> =>
        client.get<InvIngredientsResponse>('/v1/inventory/ingredients', { ...extra(o), query: query ?? {} }),
      stock: (
        query: { branchId?: string; warehouseId?: string; lowOnly?: boolean; limit?: number },
        o?: Extra,
      ): Promise<StockResponse> => client.get<StockResponse>('/v1/inventory/stock', { ...extra(o), query }),
      purchaseOrders: (
        query?: { status?: string; supplierId?: string; warehouseId?: string; branchId?: string },
        o?: Extra,
      ): Promise<InvPoListResponse> =>
        client.get<InvPoListResponse>('/v1/inventory/purchase-orders', { ...extra(o), query: query ?? {} }),
      purchaseOrder: (id: string, o?: Extra): Promise<InvPoDetailResponse> =>
        client.get<InvPoDetailResponse>(`/v1/inventory/purchase-orders/${seg(id)}`, extra(o)),

      /** Nhận hàng. Idempotency-Key REQUIRED (the client sends one by default). */
      createReceipt: (body: ReceiptBody, o?: Extra): Promise<ReceiptResponse> =>
        client.post<ReceiptResponse>('/v1/inventory/receipts', { ...extra(o), body }),

      /** Xuất nguyên liệu. Idempotency-Key REQUIRED. */
      createIssue: (body: IssueBody, o?: Extra): Promise<IssueResponse> =>
        client.post<IssueResponse>('/v1/inventory/issues', { ...extra(o), body }),

      /** Báo hao hụt. Idempotency-Key AND `x-action-reason` both required. */
      createWastage: (body: WastageBody, reason: string, o?: Extra): Promise<WastageResponse> =>
        client.post<WastageResponse>('/v1/inventory/wastages', { ...extra(o), body, reason }),

      counts: (
        query?: { branchId?: string; warehouseId?: string; status?: string },
        o?: Extra,
      ): Promise<StockCountsResponse> =>
        client.get<StockCountsResponse>('/v1/inventory/counts', { ...extra(o), query: query ?? {} }),
      count: (id: string, o?: Extra): Promise<StockCountDetailResponse> =>
        client.get<StockCountDetailResponse>(`/v1/inventory/counts/${seg(id)}`, extra(o)),
      /** Freeze a new count sheet. 409 when one is already open for the warehouse. */
      startCount: (
        body: { branchId: string; warehouseId: string; countType?: string; note?: string | null },
        o?: Extra,
      ): Promise<StockCountStartResponse> =>
        client.post<StockCountStartResponse>('/v1/inventory/counts', { ...extra(o), body }),
      saveCountItems: (id: string, body: CountItemsInput, o?: Extra): Promise<StockCountDetailResponse> =>
        client.put<StockCountDetailResponse>(`/v1/inventory/counts/${seg(id)}/items`, {
          ...extra(o),
          body,
          idempotent: false,
        }),
      submitCount: (id: string, o?: Extra): Promise<{ count: StockCountDetailResponse['count'] }> =>
        client.post<{ count: StockCountDetailResponse['count'] }>(`/v1/inventory/counts/${seg(id)}/submit`, {
          ...extra(o),
          body: {},
        }),
    },

    // -----------------------------------------------------------------------
    // realtime (routes/realtime.ts) — mints the BranchHub connect ticket
    // -----------------------------------------------------------------------
    realtime: {
      /**
       * 60-second connect ticket. NOTE: gated on `branch.view`, which pure
       * kitchen/bar roles do NOT hold — a 403 here means "poll instead", not
       * an error worth surfacing.
       */
      token: (body: { branchId: string; topics?: string[] }, o?: Extra): Promise<RealtimeTokenResponse> =>
        client.post<RealtimeTokenResponse>('/v1/realtime/token', { ...extra(o), body, idempotent: false }),
    },

    // -----------------------------------------------------------------------
    // payments (routes/payments.ts)
    // -----------------------------------------------------------------------
    payments: {
      /**
       * One call per method. A split payment is N calls, each with its OWN
       * idempotency key and a partial `amountMinor` — there is no batch route.
       */
      create: (body: CreatePaymentBody, o?: Extra): Promise<CreatePaymentResponse> =>
        client.post<CreatePaymentResponse>('/v1/payments', { ...extra(o), body }),

      refund: (
        paymentId: string,
        body: { amountMinor: number; refundMethod?: string; externalTxnId?: string | null },
        reason: string,
        o?: Extra,
      ): Promise<{ id: string; paymentId: string; amountMinor: number; paymentStatus: string }> =>
        client.post(`/v1/payments/${seg(paymentId)}/refund`, { ...extra(o), body, reason }),

      invoice: (
        orderId: string,
        body: { invoiceType?: string; buyerName?: string | null; buyerTaxCode?: string | null },
        o?: Extra,
      ): Promise<InvoiceResponse> =>
        client.post<InvoiceResponse>(`/v1/orders/${seg(orderId)}/invoice`, { ...extra(o), body, idempotent: false }),

      reprint: (
        invoiceId: string,
        body: { printerId?: string | null; printType?: string },
        reason: string,
        o?: Extra,
      ): Promise<{ id: string; invoiceId: string; printCount: number; printType: string }> =>
        client.post(`/v1/invoices/${seg(invoiceId)}/reprint`, { ...extra(o), body, reason, idempotent: false }),

      openCashSession: (
        body: { branchId: string; terminalId?: string | null; openingFloatMinor?: number },
        o?: Extra,
      ): Promise<OpenCashSessionResponse> =>
        client.post<OpenCashSessionResponse>('/v1/cash-sessions', { ...extra(o), body, idempotent: false }),

      closeCashSession: (
        id: string,
        body: { countedCashMinor: number; closingNote?: string | null },
        o?: Extra,
      ): Promise<CloseCashSessionResponse> =>
        client.post<CloseCashSessionResponse>(`/v1/cash-sessions/${seg(id)}/close`, {
          ...extra(o),
          body,
          idempotent: false,
        }),

      cashSession: (id: string, o?: Extra): Promise<CashSessionDetailResponse> =>
        client.get<CashSessionDetailResponse>(`/v1/cash-sessions/${seg(id)}`, extra(o)),

      cashMovement: (
        body: {
          cashRegisterSessionId: string;
          movementType: string;
          amountMinor: number;
          referenceType?: string | null;
          drawerOpened?: boolean;
        },
        reason: string,
        o?: Extra,
      ): Promise<{ id: string; movementType: string; amountMinor: number }> =>
        client.post('/v1/cash-movements', { ...extra(o), body, reason, idempotent: false }),
    },

    // -----------------------------------------------------------------------
    // production (routes/production.ts) — KDS and bar
    // -----------------------------------------------------------------------
    production: {
      board: (stationId: string, o?: Extra): Promise<KdsBoardResponse> =>
        client.get<KdsBoardResponse>(`/v1/stations/${seg(stationId)}/tickets`, extra(o)),
      load: (stationId: string, o?: Extra): Promise<StationLoadResponse> =>
        client.get<StationLoadResponse>(`/v1/stations/${seg(stationId)}/load`, extra(o)),

      /** Ticket level. `accept` REQUIRES an Idempotency-Key (409 on a lost race). */
      acceptTicket: (id: string, o?: Extra): Promise<ProductionActionResponse> =>
        client.post<ProductionActionResponse>(`/v1/tickets/${seg(id)}/accept`, { ...extra(o), body: {} }),
      startTicket: (id: string, o?: Extra): Promise<ProductionActionResponse> =>
        client.post<ProductionActionResponse>(`/v1/tickets/${seg(id)}/start`, { ...extra(o), body: {} }),
      completeTicket: (id: string, o?: Extra): Promise<ProductionActionResponse> =>
        client.post<ProductionActionResponse>(`/v1/tickets/${seg(id)}/complete`, { ...extra(o), body: {} }),

      /** Item level. */
      acceptItem: (id: string, o?: Extra): Promise<ProductionActionResponse> =>
        client.post<ProductionActionResponse>(`/v1/ticket-items/${seg(id)}/accept`, { ...extra(o), body: {} }),
      startItem: (id: string, o?: Extra): Promise<ProductionActionResponse> =>
        client.post<ProductionActionResponse>(`/v1/ticket-items/${seg(id)}/start`, {
          ...extra(o),
          body: {},
          idempotent: false,
        }),
      completeItem: (id: string, o?: Extra): Promise<ProductionActionResponse> =>
        client.post<ProductionActionResponse>(`/v1/ticket-items/${seg(id)}/complete`, {
          ...extra(o),
          body: {},
          idempotent: false,
        }),
      pauseItem: (id: string, reason: string, o?: Extra): Promise<ProductionActionResponse> =>
        client.post<ProductionActionResponse>(`/v1/ticket-items/${seg(id)}/pause`, {
          ...extra(o),
          body: {},
          reason,
          idempotent: false,
        }),
      resumeItem: (id: string, o?: Extra): Promise<ProductionActionResponse> =>
        client.post<ProductionActionResponse>(`/v1/ticket-items/${seg(id)}/resume`, {
          ...extra(o),
          body: {},
          idempotent: false,
        }),
      pickupItem: (id: string, o?: Extra): Promise<ProductionActionResponse> =>
        client.post<ProductionActionResponse>(`/v1/ticket-items/${seg(id)}/pickup`, {
          ...extra(o),
          body: {},
          idempotent: false,
        }),
      deliverItem: (id: string, o?: Extra): Promise<ProductionActionResponse> =>
        client.post<ProductionActionResponse>(`/v1/ticket-items/${seg(id)}/deliver`, {
          ...extra(o),
          body: {},
          idempotent: false,
        }),
      missingIngredient: (
        id: string,
        body: { ingredientNote: string; requestRestock?: boolean },
        reason: string,
        o?: Extra,
      ): Promise<ProductionActionResponse> =>
        client.post<ProductionActionResponse>(`/v1/ticket-items/${seg(id)}/missing-ingredient`, {
          ...extra(o),
          body,
          reason,
          idempotent: false,
        }),
      returnItem: (
        id: string,
        body: { reasonCode: string; quantity?: number; actionTaken?: string; responsibleParty?: string },
        reason: string,
        o?: Extra,
      ): Promise<ProductionActionResponse> =>
        client.post<ProductionActionResponse>(`/v1/ticket-items/${seg(id)}/return`, {
          ...extra(o),
          body,
          reason,
          idempotent: false,
        }),
      remakeItem: (
        id: string,
        body: { reasonCode: string; responsibleParty?: string; ingredientRecharged?: boolean },
        reason: string,
        o?: Extra,
      ): Promise<ProductionActionResponse & { replacementItemId: string }> =>
        client.post(`/v1/ticket-items/${seg(id)}/remake`, { ...extra(o), body, reason, idempotent: false }),

      /** "Hết món" straight from the pass. Needs `product.availability.manage`. */
      eightySix: (
        stationId: string,
        body: {
          productId: string;
          variantId?: string | null;
          action?: 'MARK_86' | 'RESTORE';
          reason?: string;
          reasonText?: string | null;
          remainingQty?: number | null;
        },
        o?: Extra,
      ): Promise<{ ok: true; productId: string; action: string; isAvailable: boolean }> =>
        client.post(`/v1/stations/${seg(stationId)}/86`, { ...extra(o), body, idempotent: false }),
    },
  };
}

export type PosApi = ReturnType<typeof createPosApi>;

// ---------------------------------------------------------------------------
// The customer QR surface — a DIFFERENT credential.
//
// It authenticates with `x-session-token` (a signed QR token), not a Bearer
// staff token, so it gets its own client instance with `anonymous: true` on
// every call. Mixing it into the staff client would attach a cashier's bearer
// token to a guest's phone request.
// ---------------------------------------------------------------------------

export function createCustomerClient(): ApiClient {
  return createApiClient({
    baseUrl: API_BASE_URL,
    getTokens: () => null,
    setTokens: () => undefined,
    onUnauthenticated: () => undefined,
    // Guests are on restaurant wifi or 3G in a basement. 20s, not 12s.
    timeoutMs: 20_000,
    clientVersion: import.meta.env.VITE_APP_VERSION ?? 'pos-dev',
  });
}

export function createCustomerApi(client: ApiClient, token: string) {
  const withToken = (o?: Extra, etag?: string | null): RequestOptions => ({
    ...extra(o),
    anonymous: true,
    headers: etag == null ? { 'x-session-token': token } : { 'x-session-token': token, 'if-none-match': etag },
  });

  return {
    session: (o?: Extra): Promise<CustomerSessionResponse> =>
      client.get<CustomerSessionResponse>('/v1/customer/session', withToken(o)),

    setLocale: (locale: string, o?: Extra): Promise<CustomerSetLocaleResponse> =>
      client.post<CustomerSetLocaleResponse>('/v1/customer/session/locale', {
        ...withToken(o),
        body: { locale },
        idempotent: false,
      }),

    menu: (o?: Extra): Promise<CustomerMenuResponse> =>
      client.get<CustomerMenuResponse>('/v1/customer/menu', withToken(o)),

    uiStrings: (locale: string | null, o?: Extra): Promise<CustomerUiStringsResponse> =>
      client.get<CustomerUiStringsResponse>('/v1/customer/ui-strings', {
        ...withToken(o),
        query: locale === null ? {} : { locale },
      }),

    /**
     * Submit an order. The API REQUIRES an Idempotency-Key (8–128 chars) and
     * replays the stored response on a repeat, which is what makes a lost
     * response on a flaky phone safe.
     */
    submitOrder: (
      body: { items: CustomerOrderItemInput[]; note?: string | null; guestCount?: number | null },
      idempotencyKey: string,
      o?: Extra,
    ): Promise<SubmitOrderResp> =>
      client.post<CustomerSubmitOrderResponse>('/v1/customer/orders', {
        ...withToken(o),
        body,
        idempotencyKey,
      }),

    orders: (o?: Extra): Promise<CustomerOrdersResponse> =>
      client.get<CustomerOrdersResponse>('/v1/customer/orders', withToken(o)),

    /** Business codes only — never free text as the code. */
    request: (
      requestCode: CustomerRequestCode,
      freeText: string | null,
      o?: Extra,
    ): Promise<CustomerServiceRequestResponse> =>
      client.post<CustomerServiceRequestResponse>('/v1/customer/requests', {
        ...withToken(o),
        body: { requestCode, freeText },
        idempotent: false,
      }),

    requests: (o?: Extra): Promise<CustomerRequestsResponse> =>
      client.get<CustomerRequestsResponse>('/v1/customer/requests', withToken(o)),

    feedback: (
      body: {
        rating: number;
        foodRating?: number | null;
        serviceRating?: number | null;
        speedRating?: number | null;
        comment?: string | null;
      },
      o?: Extra,
    ): Promise<CustomerFeedbackResponse> =>
      client.post<CustomerFeedbackResponse>('/v1/customer/feedback', { ...withToken(o), body, idempotent: false }),
  };
}

export type CustomerApi = ReturnType<typeof createCustomerApi>;

export { newIdempotencyKey };

// ---------------------------------------------------------------------------
// ENDPOINTS THE POS NEEDS AND THE API DOES NOT HAVE
//
// Every one of these is rendered in the UI as an explicit
// "Chức năng đang phát triển — cần endpoint X" panel. None of them is faked.
//
//  1. GET /v1/orders/:id                — no single-order detail. A takeaway
//     order with no dining session has NO way to fetch its line items.
//  2. GET /v1/dining-sessions           — cannot list open tables from orders;
//     the floor map is the only discovery path.
//  3. GET /v1/payments, /v1/payments/:id — no payment list/detail. The cash
//     session report is the only aggregated view.
//  4. GET /v1/invoices, /v1/invoices/:id — after issuing, only the returned id
//     is ever held. No rendered receipt payload (no ESC/POS, no PDF) either, so
//     the POS composes the 80mm layout itself from order + item data.
//  5. GET /v1/cash-sessions              — no list. An open till can only be
//     discovered from the 409 `details.cashRegisterSessionId` when opening a
//     second one, which is why the id is cached in localStorage.
//  6. No payment-intent / VietQR generation and no PSP webhook: QR_BANK and
//     EWALLET are recorded AFTER a human confirms receipt.
//  7. No public WebSocket route for the BranchHub: `POST /v1/realtime/token`
//     mints the ticket and the Durable Object implements `/connect`, but the
//     Worker exposes no `GET /v1/realtime/connect` upgrade proxy to the DO
//     stub. `src/lib/realtime.ts` dials that path and degrades to polling
//     until it exists. Also: the token mint is gated on `branch.view`, which
//     pure kitchen/bar roles do not hold — those boards keep polling.
//
// FIXED since the last revision (now wired above): device self-claim
// (`POST /devices/claim`, `GET /devices/me`), QR slip issuing
// (`POST /dining-sessions/:id/qr-token`), table clean/setup, the pickup-queue
// feed, the approvals inbox and the whole warehouse slice of /inventory.
// ---------------------------------------------------------------------------
